// @flow
import { post, get } from './common';
import type { CartState } from '../reducers/cart';
import { parseCartForServer } from './cart';
import config from '../config';
import { handleAPIErrors } from '../sentry-util';

export const getCartPaymentMethods = async (
	{
		mobile,
		sessionId,
		cart,
		invoiceAmount = 0,
		addressId,
		affiliateName,
	}: {
		mobile: string,
		sessionId: string,
		cart: CartState,
		invoiceAmount: number,
		addressId: string,
		affiliateName:? string,
	},
	session: string
) => {
	let body = {};
	try {
		if (!mobile || !sessionId || !cart || !invoiceAmount) {
			throw new Error('Insufficient data provided');
		}

		// console.log(cart);
		body = {
			user_id: mobile,
			session_id: sessionId,
			vm_id: cart.machine ? cart.machine.vm_id : '',
			deviceType: 'app',
			...parseCartForServer(cart),
			invoice_amount: String(invoiceAmount),
			affiliate_name: affiliateName,
			address_id: addressId,
		};

		const res = await post('/customer/app/paymentlist', body, { session });

		const { status, data = {}, msg } = res;
		if (!status) {
			throw new Error(msg || '');
		}

		const { order_id, list } = data;
		if (!order_id || !list) {
			throw new Error('Invalid data');
		}
		return {
			paymentMethods: processPaymentMethods(list),
			orderId: order_id,
		};
	} catch (error) {
		handleAPIErrors(error, '/customer/app/paymentlist', 'POST', body);
		throw error;
	}
};

export const getOrderPaymentMethods = async (reqBody: { mobile: string, amount: string, orderId: string }) => {
	try {
		const res = await post('/customer/app/paymentlist', reqBody);
		return processPaymentMethods(res);
	} catch (error) {
		handleAPIErrors(error, '/customer/app/paymentlist', 'POST', reqBody);
		throw error;
	}
};

const processPaymentMethods = (paymentMethods: Array<any> = [], type: 'CART' | 'WALLET' = 'CART'): Array<{}> => {
	if (paymentMethods.length === 0) {
		return [];
	}

	paymentMethods.sort((payment1, payment2) => {
		const { header } = payment1;
		return header === 'UPI' ? -1 : 0;
	});

	return paymentMethods.reduce((paymentArray, paymentMethod) => {
		const paymentType = paymentMethod.label;
		const { payments } = paymentMethod;
		// if (!payments || payments.length === 0) {
		//   return paymentArray;
		// }

		const filterPayments = payments.reduce((filteredPayments, payment) => {
			if (paymentType === 'cards' || payment.active) {
				// if (type === 'CART') {
				//   payment.status = true;
				// }

				filteredPayments.push({
					...payment,
					paymentType,
					id: paymentType === 'cards' ? payment.card_token : payment.name,
				});
			}
			return filteredPayments;
		}, []);

		return [...paymentArray, ...filterPayments];
	}, []);
};

export const linkPaymentSendOTP = async (
	{
		mobile,
		paymentMethod,
		imei,
	}: {
		mobile: string,
		paymentMethod: { name: string },
		imei: string,
	},
	session: string
) => {
	let url = '';
	let reqBody = {};
	try {
		let url = getPaymentURL(paymentMethod.name);
		if (!url) {
			throw new Error('No payment method selected');
		}

		reqBody = paymentMethod.name === 'LazyPay' ? [{ mobile }, { imei }] : { mobile, imei };
		const res = await post(url, reqBody, { session });
		const { status = false, msg } = res;
		if (!status) {
			throw new Error(msg || '');
		}

		return res;
	} catch (error) {
		handleAPIErrors(error, url, 'POST', reqBody);
		throw error;
	}
};

export const linkPaymentMethod = async (
	{
		mobile,
		otp,
		paymentMethod,
		imei,
	}: {
		mobile: string,
		otp: string,
		paymentMethod: { name: string, state: string },
		imei: string,
	},
	session: string
) => {
	let url = '';
	let reqBody = {};
	try {
		url = getPaymentURL(paymentMethod.name, 'VALIDATE');
		if (!url) {
			throw new Error('No payment method selected');
		}

		let reqBody = { mobile, otp, state: paymentMethod.state, imei };
		if (paymentMethod.name === 'LazyPay') {
			reqBody = [{ mobile }, {}, { OTP: otp }, { imei }];
		}

		const res = await post(url, reqBody, { session });
		const { status = false, msg } = res;
		if (!status) {
			throw new Error(msg || '');
		}

		return res;
	} catch (error) {
		handleAPIErrors(error, url, 'POST', reqBody);
		throw error;
	}
};

const getPaymentURL = (name = '', type: 'SEND' | 'VALIDATE' = 'SEND') => {
	switch (name) {
		case 'PayTM_WALLET':
			if (type === 'SEND') {
				return '/payment/paytmauto/sendOtp';
			}
			return '/payment/paytmauto/validateOtp';

		case 'LazyPay':
			if (type === 'SEND') {
				return '/payment/lazypay/initiate';
			}
			return '/payment/lazypay/validateOtp';

		case 'Ola':
			if (type === 'SEND') {
				return '/payment/ola/sendOtp';
			}
			return '/payment/ola/validateOtp';

		default:
			return '';
	}
};

export const makePayment = async (
	{
		mobile,
		paymentMethod,
		amount,
		type,
		orderId,
		deliveryFee = 0
	}: {
		mobile: string,
		paymentMethod: { name: string },
		amount: string,
		type: string,
		orderId: string,
		deliveryFee: number,
	},
	session: string
) => {
	let reqBody = {};
	try {
		reqBody = createReqBody(paymentMethod, mobile, amount, deliveryFee, type, orderId);
		const res = await post(`${config.apiPath}/makePayment`, reqBody, { session });
		const { status = false, msg } = res;
		if (!status && !(paymentMethod.name === 'PayTM_WALLET' || paymentMethod.name === 'Ola')) {
			throw new Error(msg || 'Error while processing payment. Please try again');
		}

		return res;
	} catch (error) {
		handleAPIErrors(error, '/makePayment', 'POST', reqBody);
		throw error;
	}
};

//FIXME: V:: Move to server side in makePayment API...
//args not required...
export const initRazorPayPayment = async (
	{
		mobile,
		paymentMethod,
		amount,
		type,
		orderId,
		deliveryFee = 0
	}: {
		mobile: string,
		paymentMethod: { name: string },
		amount: string,
		type: string,
		orderId: string,
		deliveryFee: number,
	},
	session: string
) => {
	let reqBody = {};
	try {
		//reqBody = createReqBody(paymentMethod, mobile, amount, deliveryFee, type, orderId);
		const res = await post(process.env.REACT_APP_RAZORPAY_API_URL+'/razorpay', {amount}, { session });
		const { status = false, msg } = res;
		if (!status && !(paymentMethod.name === 'PayTM_WALLET' || paymentMethod.name === 'Ola')) {
			throw new Error(msg || 'Error while processing payment. Please try again');
		}

		return res;
	} catch (error) {
		handleAPIErrors(error, '/makePayment', 'POST', {});
		throw error;
	}
};

export const processPaytmAddMoney = (paytmData: any) => {
	const createform = document.createElement('form');
	createform.setAttribute('action', paytmData.url);
	createform.setAttribute('method', 'post');
	createform.setAttribute('id', 'paytm');
	Object.keys(paytmData.data).forEach((item) => {
		const input = document.createElement('input');
		input.setAttribute('type', 'hidden');
		input.setAttribute('name', item);
		input.setAttribute('value', paytmData.data[item]);
		createform.append(input);
	});

	document.body && document.body.appendChild(createform);
	createform.submit();
};

const createReqBody = (paymentMethod: any, mobile: string, amount: string, deliveryFee: number = 0, type: string = 'CART', ...rest) => {
	let req = ({
		payment_method: paymentMethod.name,
		mobile: mobile,
		amount: amount,
		vpa: paymentMethod.vpa,
		delivery_fee: deliveryFee,
		deviceType: process.env.REACT_APP_DEVICE_TYPE || '',
	}: { [key: string]: string | number });

	if (type === 'CART') {
		req.orderId = rest[0];
	}
	if (paymentMethod.name === "razor_pay") {
		req.status = rest[1];
		req.pg_data = rest[2];
	}

	if (paymentMethod.name === 'Ola') {
		req = {
			...req,
			...paymentMethod,
			param: 'd', // for payment redirection
		};
	} else if (paymentMethod.paymentType === 'cards') {
		req = {
			...req,
			payment_method: 'juspay',
		};

		if ('card_token' in paymentMethod) {
			req = {
				...req,
				card_security_code: paymentMethod.card_security_code,
				card_token: paymentMethod.card_token,
			};
		} else {
			req = {
				...req,
				...paymentMethod,
			};

			delete req.type;
		}
	}

	return req;
};

export const capturePayment = async (
	{
		mobile,
		paymentMethod,
		amount,
		type,
		orderId,
		deliveryFee = 0,
		paymentDetails,
	}: {
		mobile: string,
		paymentMethod: { name: string },
		amount: string,
		type: string,
		orderId: string,
		deliveryFee: number,
		paymentDetails: { status: string, pg_data: Object },
	},
	session: string
) => {
	let reqBody = {};
	let apiUrl = '';
	try {
		//console.log("*** gr8 status: "+paymentDetails.status);
		reqBody = createReqBody(paymentMethod, mobile, amount, deliveryFee, type, orderId, paymentDetails.status, paymentDetails.pg_data);
		apiUrl = `${config.apiPath}/capturePayment`;
		if (paymentMethod.name === 'Ola') {
			apiUrl = '/customer/app/capturePaymentOla';
		} else if (paymentMethod.name === 'razor_pay') {//FIXME: V:: Use common capturePayment n send PG related data...
			apiUrl = '/customer/app/v2/capturePaymentRazorPay';
		}
		const res = await post(apiUrl, reqBody, { session });
		const { status = false, msg } = res;
		if (!status) {
			throw new Error(msg || 'Error while processing payment. Please try again');
		}

		return res;
	} catch (error) {
		handleAPIErrors(error, apiUrl, 'POST', reqBody);
		throw error;
	}
};

export const fetchPendingBalance = async (mobile: string, session: string) => {
	try {
		const res = await get('/customer/app/pendingAmount', { user_id: mobile }, { session });
		const { status = false, msg, data } = res;
		if (!status) {
			throw new Error(msg || 'Error while fetching payment data');
		}

		return data;
	} catch (error) {
		handleAPIErrors(error, '/customer/app/pendingAmount', 'GET', { user_id: mobile });
		throw error;
	}
};

export const getWalletPaymentMethods = async (
	{
		mobile,
		sessionId,
		amount,
		imei = '',
	}: {
		mobile: string,
		sessionId: string,
		amount: number,
		imei: string
	},
	session: string
) => {
	try {
		if (!mobile || !sessionId || !amount) {
			throw new Error('Insufficient data provided');
		}

		// console.log(cart);
		const res = await post(
			'/customer/wallet/getPaymentList',
			{
				mobile,
				session_id: sessionId,
				amount,
				imei
			},
			{ session }
		);

		const { status, data = {}, msg } = res;
		if (!status) {
			throw new Error(msg || '');
		}

		const { list } = data;
		if (!list) {
			throw new Error('Invalid data');
		}
		return {
			paymentMethods: processPaymentMethods(list, 'WALLET'),
		};
	} catch (error) {
		handleAPIErrors(error, '/customer/wallet/getPaymentList', 'POST', {
			mobile,
			session_id: sessionId,
			amount,
		});
		throw error;
	}
};

export const neuuWalletPayment = async (
	{
		mobile,
		sessionId,
		amount,
		cart,
		addressId,
		affiliateName,
		deliveryFee
	}: {
		mobile: string,
		sessionId: string,
		amount: number,
		cart: CartState,
		addressId: string,
		deliveryFee: number,
		affiliateName:? string
	},
	session: string
) => {
	let reqBody = {};

	try {
		if (!mobile || !sessionId || !cart || !amount) {
			throw new Error('Insufficient data provided');
		}

		reqBody = {
			user_id: mobile,
			session_id: sessionId,
			amount,
			vm_id: cart.machine ? cart.machine.vm_id : '',
			deviceType: 'app',
			address_id: addressId,
			affiliate_name: affiliateName,
			delivery_fee: deliveryFee,
			...parseCartForServer(cart),
		};
		const res = await post('/customer/app/neuuWalletPay', reqBody, { session });

		const { status, msg } = res;
		if (!status) {
			throw new Error(msg || '');
		}

		return res;
	} catch (error) {
		handleAPIErrors(error, '/customer/app/neuuWalletPay', 'POST', reqBody);
		throw error;
	}
};

export const checkPaymentStatus = async (
	reqBody: {
		mobile: string,
		payment_method: string,
		amount: string,
		type: string,
		orderId: string,
		txn_id: string,
		param: string,
	},
	session: string
) => {
	try {
		const res = await post('/customer/app/getUpiTransactionStatus', reqBody, { session });
		return res;
	} catch (error) {
		handleAPIErrors(error, '/getUpiTransactionStatus', 'POST', reqBody);
		throw error;
	}
};

export const validateVpa = async (vpa: string, imei: string, session: string) => {
	try {
		const res = await get('/customer/app/validateVpa', { vpa, imei }, { session });
		return res;
	} catch (error) {
		handleAPIErrors(error, '/validateVpa', 'GET', { vpa, imei });
		throw error;
	}
};

export const clearVpa = async (mobile: string, gateway: string, session: string) => {
	try {
		const res = await post(
			'/customer/app/clearVpa',
			{
				mobile,
				gateway,
			},
			{ session }
		);

		const { status, msg } = res;
		if (!status) {
			throw new Error(msg || 'Error while clearing your vpa.');
		}
		return res;
	} catch (error) {
		handleAPIErrors(error, '/clearVpa', 'POST', { mobile, gateway });
		throw error;
	}
};

export const pgAddMoney = async (
	{
		mobile,
		paymentMethod,
		amount,
		type,
		orderId,
		param = '',
	}: {
		mobile: string,
		paymentMethod: { name: string },
		amount: string,
		type: string,
		orderId: string,
		param: string
	},
	session: string
) => {
	try {
		const reqBody = {
			payment_method : paymentMethod.name,
			amount,
			mobile,
			orderId,
			param
		};
		
		const res = await post('/customer/app/addPendingPayment ', reqBody, { session });
		const { status = false, msg } = res;
		if (!status) {
			throw new Error(msg || 'Error while processing payment. Please try again');
		}

		return res;
	} catch (error) {
		throw error;
	}
};
